/**
 * RoomStatRow — one room's vitals inside a network tile's inline expansion.
 * Same metric vocabulary as the network tile, scoped to a room. Click → open the
 * room. Data is one `roomVitals` object from `GET /networks/{id}/rooms-dashboard`.
 */
import React from 'react';
import { Users, Server } from 'lucide-react';
import {
  SuccessRing, ConsumptionMeter, OutcomeChip,
} from '@stepflow/ui-shared/components/dashboard/vitalWidgets.jsx';
import RoomAvatar from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';

export default function RoomStatRow({ room, networkId, onOpen, unit = 'usd' }) {
  const r = room || {};
  return (
    <button
      type="button"
      onClick={() => onOpen?.(networkId, r.poolId)}
      className="flex w-full items-center gap-2.5 rounded-lg border border-gray-100 bg-white px-2.5 py-1.5 text-left hover:border-blue-200 hover:bg-blue-50/40"
      data-testid={`room-stat-row-${networkId}-${r.poolId}`}
    >
      {/* Room visual — CIRCLE in the network's hue family (lineage), with a live
          pulse dot overlaid when the room is running. */}
      <span className="relative shrink-0">
        <RoomAvatar networkId={networkId} poolId={r.poolId} name={r.name} size={22} />
        {r.isActive ? (
          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 animate-pulse rounded-full border border-white bg-emerald-500" aria-hidden />
        ) : null}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800">
        {r.name || r.poolId}
        {r.stepflowVersion ? (
          <span className="ml-1.5 text-[10px] font-normal text-gray-400">{r.stepflowVersion}</span>
        ) : null}
      </span>
      <OutcomeChip pct={r.outcomePct} />
      <span className="flex items-center gap-1 text-[10px] text-gray-500" title="Agents">
        <Users className="h-3 w-3" aria-hidden />{r.agentCount ?? 0}
      </span>
      <span title="Success rate (lifetime)"><SuccessRing pct={r.successRatePct} size={22} /></span>
      <span className="flex w-24 items-center gap-1"
            title={unit === 'usd' ? 'Estimated cost consumed' : 'Points consumed'}>
        <Server className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
        {/* Unit ($/pts) is driven by the fleet-footer toggle — one control for the
            whole dashboard, so room rows stay uncluttered (no per-row dropdown). */}
        <ConsumptionMeter unit={unit} consumed={r.consumedPoints} consumedUsd={r.consumedUsd} budget={0} />
      </span>
    </button>
  );
}
